<script>
import whatapp from "@/assets/images/mon-compte/icons/whatapp.svg";
import messenger from "@/assets/images/mon-compte/icons/messenger.svg";
import { useClientStore } from "@/store/client";
import { useAuthStore } from "@/store/auth";
import { mapActions, mapState } from "pinia";

import ModifierEmail from "@/containers/mon-compte/mon-profil/modifier-email";
import ModifierMotDePasse from "@/containers/mon-compte/mon-profil/modifier-mot-de-passe";
import ModifierInformations from "@/containers/mon-compte/mon-profil/modifier-informations";
import SuppressionDeCompte from "@/containers/mon-compte/mon-profil/suppression-de-compte";
import LazyImage from "@/components/atoms/lazy-image";
import Icon from "@/components/atoms/icon";
import Button from "@/components/atoms/button";
import ButtonIcon from "@/components/molecules/button-icon";
import Badge from "@/components/atoms/badge";
import FormRadio from "@/components/molecules/form-radio";
import FormGroup from "@/components/molecules/form-group";
import Divider from "@/components/atoms/divider";
import Form from "@/components/molecules/form";
import Popin from "@/components/molecules/popin";
import Card from "@/components/molecules/card";
import StateOverlay from "@/components/molecules/state-overlay";
import NewsletterSelector from "@/containers/newsletter-selector";
import footerCodes from "@/utils/footer-codes";
import { useInteractionDoneTracking } from "@/mixins/tracking/interaction-done";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";

export default {
  name: "MonProfil",
  components: {
    LazyImage,
    Icon,
    Button,
    Badge,
    FormRadio,
    FormGroup,
    Divider,
    ButtonIcon,
    Form,
    ModifierEmail,
    Popin,
    ModifierMotDePasse,
    ModifierInformations,
    SuppressionDeCompte,
    Card,
    StateOverlay,
    NewsletterSelector
  },
  props: {
    sticky: {
      type: Boolean,
      required: false,
      default: false
    },
    ...formValidationProps()
  },
  setup(props) {
    const { v$ } = useFormValidation(props);
    const { trackEvent } = useInteractionDoneTracking();
    return { trackEvent, whatapp, messenger, v$ };
  },
  data() {
    return {
      isLoading: false,
      suppressionConfirmee: false,
      model: {
        optin_sms_suivi_commande_drive: false
      },
      footerCodes
    };
  },
  computed: {
    ...mapState(useClientStore, ["client", "isPro", "contratPro"]),
    civiliteFormatted() {
      if (this.client && this.client.attributes) {
        if (this.client.attributes.civilite === "M.") {
          return "Monsieur";
        } else if (this.client.attributes.civilite === "Mme") {
          return "Madame";
        }
        return this.client.attributes.civilite;
      }
      return null;
    }
  },
  mounted() {
    if (this.client) {
      this.hydrateModel(this.client.attributes);
    }
  },
  methods: {
    ...mapActions(useClientStore, ["update"]),
    ...mapActions(useAuthStore, ["logout"]),
    hydrateModel(client) {
      this.model.optin_sms_suivi_commande_drive =
        client.optin_sms_suivi_commande_drive;
    },
    async save() {
      try {
        this.isLoading = true;
        await this.update(this.model);
      } catch (err) {
        this.error = this.$apiDrive.getErrorData(err);
        this.hydrateModel(this.client.attributes);
      } finally {
        this.isLoading = false;
      }
    },
    infosUpdated() {
      this.$refs.modifierInformations.hide();
      this.hydrateModel(this.client.attributes);
    },
    mailUpdated() {
      this.$refs.modifierEmail.hide();
      this.hydrateModel(this.client.attributes);
      this.$refs.newsletter.reload();
    },
    passwordUpdated() {
      this.$refs.modifierMotDePasse.hide();
    },
    async closeSuppression() {
      if (this.suppressionConfirmee) {
        await this.logout();
        this.$router.replace("/");
      }
    },
    socialMediaLinkClicked(app) {
      this.trackEvent({
        categoryGA: trackingCodes.PAGETYPE.clientAccount,
        actionGA: trackingCodes.ACTIONGA.socialMedia,
        labelGA: app
      });
    }
  }
};
</script>

<template>
  <div>
    <Popin
      ref="suppressionDeCompte"
      aria-label="Suppression de compte"
      :page-back="sticky"
      close-on-route-change
      @closed="closeSuppression"
    >
      <SuppressionDeCompte
        class="u-full-height"
        @close="$refs.suppressionDeCompte.hide()"
        @confirmation="suppressionConfirmee = true"
      />
    </Popin>

    <Popin
      ref="modifierEmail"
      aria-label="Modifier l'email"
      :page-back="sticky"
    >
      <ModifierEmail
        class="u-full-height"
        @updated="mailUpdated"
      />
    </Popin>

    <Popin
      ref="modifierMotDePasse"
      aria-label="Modifier le mot de passe"
      :page-back="sticky"
    >
      <ModifierMotDePasse @updated="passwordUpdated" />
    </Popin>

    <Popin
      ref="modifierInformations"
      aria-label="Modifier les informations"
      :page-back="sticky"
    >
      <ModifierInformations @updated="infosUpdated" />
    </Popin>

    <h1 class="u-color-primary u-font-secondary u-font-24 u-font-700 u-mb-md u-ml-sm@sm">
      Mon profil
    </h1>
    <Card
      radius="md"
      elevation="sm"
      class="u-p-md"
    >
      <StateOverlay
        :is-loading="isLoading"
        overlay
      >
        <Form
          :error="error"
          class="c-mon-profil"
          @submit.prevent="save"
        >
          <h2 class="c-mon-profil-category-title">
            <Badge
              variant="primary"
              circle
              size="lg"
              class="u-mr-sm"
            >
              <Icon
                name="lock"
                variant="white"
                size="sm"
              />
            </Badge>
            Mes identifiants
          </h2>
          <div class="c-mon-profil-category">
            <div class="u-flex u-flex-justify-between@sm">
              <p>
                Adresse email :
                <br class="u-none u-inline@sm">
                <span class="u-ml-xs u-ml-none@sm">{{ client.attributes.email }}</span>
              </p>

              <Button
                v-if="!isPro"
                link
                no-underline
                class="u-ml-sm u-none@sm"
                @click="$refs.modifierEmail.show()"
              >
                Modifier
              </Button>
              <ButtonIcon
                v-if="!isPro"
                link
                no-underline
                icon-name="pen"
                icon-color="text-light"
                variant="text-light"
                class="u-ml-sm u-none u-inline-flex@sm"
                @click="$refs.modifierEmail.show()"
              >
                Modifier
              </ButtonIcon>
            </div>
            <div class="u-flex u-flex-justify-between@sm u-mt-sm">
              <p>
                Mot de passe :
                <br class="u-none u-inline@sm">
                <span class="u-ml-xs u-ml-none@sm">••••••••••••</span>
              </p>
              <Button
                v-if="!isPro"
                link
                no-underline
                class="u-ml-sm u-none@sm"
                @click="$refs.modifierMotDePasse.show()"
              >
                Modifier
              </Button>
              <ButtonIcon
                v-if="!isPro"
                link
                no-underline
                icon-name="pen"
                icon-color="text-light"
                variant="text-light"
                class="u-ml-sm u-none u-inline-flex@sm"
                @click="$refs.modifierMotDePasse.show()"
              >
                Modifier
              </ButtonIcon>
            </div>
          </div>

          <Divider
            class="u-none u-block@md"
            dotted
          />
          <h2 class="c-mon-profil-category-title u-mt-md@md">
            <Badge
              variant="primary"
              circle
              size="lg"
              class="u-mr-sm"
            >
              <Icon
                name="user"
                variant="white"
                size="sm"
              />
            </Badge>
            Mes informations personnelles
            <Button
              v-if="!isPro"
              link
              no-underline
              class="u-ml-md u-font-400 u-none@sm"
              @click="$refs.modifierInformations.show()"
            >
              Modifier
            </Button>
          </h2>
          <div class="c-mon-profil-category">
            <template v-if="contratPro">
              <p>
                Etablissement : <span class="u-ml-xs">{{ contratPro.nom_contrat }}</span>
              </p>
              <p class="u-mt-sm u-mb-sm">
                N° de carte : <span class="u-ml-xs">{{ contratPro.num_carte_pro }}</span>
              </p>
            </template>
            <p>
              Civilité : <span class="u-ml-xs">{{ civiliteFormatted }}</span>
            </p>
            <p class="u-mt-sm">
              Nom : <span class="u-ml-xs">{{ client.attributes.nom }}</span>
            </p>
            <p class="u-mt-sm">
              Prénom : <span class="u-ml-xs">{{ client.attributes.prenom }}</span>
            </p>
            <p class="u-mt-sm">
              Téléphone : <span class="u-ml-xs">{{ client.attributes.tel }}</span>
            </p>
            <div
              v-if="!isPro"
              class="u-mt-lg u-mt-sm@md u-mb-lg25 u-mb-lg20@md"
            >
              <p class="u-mb-sm">
                Souhaitez-vous être informé(e) par SMS de l'évolution de vos commandes ?
              </p>
              <FormGroup inline>
                <FormRadio
                  v-model="model.optin_sms_suivi_commande_drive"
                  name="sms_suivi_commande"
                  :value="true"
                  class="u-mr-lg"
                  @change="save()"
                >
                  Oui
                </FormRadio>
                <FormRadio
                  v-model="model.optin_sms_suivi_commande_drive"
                  name="sms_suivi_commande"
                  :value="false"
                  @change="save()"
                >
                  Non
                </FormRadio>
              </FormGroup>
            </div>
            <div class="u-text-center u-mt-md@sm">
              <ButtonIcon
                v-if="!isPro"
                link
                no-underline
                icon-name="pen"
                icon-color="text-light"
                variant="text-light"
                class="u-none u-inline-flex@sm"
                @click="$refs.modifierInformations.show()"
              >
                Modifier mes informations
              </ButtonIcon>
            </div>
          </div>
          <Divider class="u-mt-xlg u-mb-md" />
        </Form>
        <template v-if="!isPro">
          <h2 class="c-mon-profil-category-title u-mb-sm">
            <Badge
              variant="primary"
              circle
              size="lg"
              class="u-mr-sm"
            >
              <Icon
                name="document"
                variant="white"
                size="sm"
              />
            </Badge>
            Mes newsletters
          </h2>
          <NewsletterSelector ref="newsletter" />
        </template>

        <Divider class="u-mt-xlg u-mb-md" />

        <div class="c-mon-profil-button-delete u-text-center@md">
          <Button
            link
            variant="text-light"
            @click="$refs.suppressionDeCompte.show()"
          >
            Supprimer mon compte Internet
          </Button>
        </div>
        <div class="c-mon-profil-button-personal-data u-text-center@md u-mt-md u-mb-xs">
          <Button
            link
            variant="text-light"
            href="https://www.cora.fr/donnees-personnelles/"
            target="_blank"
          >
            Données Personnelles - Exercez vos droits
          </Button>
        </div>
      </StateOverlay>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
.c-mon-profil-category {
  padding-left: 5rem;
  margin-top: spacing(sm);
  margin-bottom: spacing(xlg);
  @include mq(bp(sm)) {
    padding-left: 0;
    margin-bottom: spacing(md);
  }
}
.c-mon-profil-category-title {
  display: flex;
  align-items: center;
  font-size: 2.1rem;
  font-family: font(secondary);
  font-weight: 700;
}
.c-mon-profil-category-subtitle,
.c-mon-profil-messagerie-subtitle {
  padding-left: 5rem;
  font-size: 1.9rem;
  font-family: font(secondary);
  font-weight: 700;
  @include mq(bp(sm)) {
    padding-left: 0;
    text-align: left;
  }
}
.c-mon-profil-button-delete,
.c-mon-profil-button-personal-data {
  padding-left: 5rem;
  @include mq(bp(md)) {
    padding-left: 0;
  }
}
.c-mon-profil-messagerie-button {
  padding-left: 5rem;
  margin-top: spacing(md);
  flex-wrap: wrap;
  gap: spacing(sm);
  @include mq(bp(md)) {
    padding-left: 0;
    margin-bottom: spacing(md);
  }
}
</style>
